import { BitkitEmptyState, BitkitPagination, BitkitTable, IconFilter, rem } from '@bitrise/bitkit-v2';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { AriaAttributes, useContext, useState } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { nextSortDirection, toSortDirection } from '@/hooks/useTableSort';
import { BuildCacheToolIds, buildCacheTools } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import InvocationDetailsDrawer from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationDetailsDrawer';
import { useInvocationListTableContext } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableContext';
import InvocationListTableRowDesktop from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableRowDesktop';
import { hidesCacheStatusColumn } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/utils/invocationCacheStatus';

type InvocationListTableDesktopProps = {
  invocations: BuildToolInvocationWithDetails[];
  startedAtSortDirection: AriaAttributes['aria-sort'];
  onStartedAtSortClick: (sortDirection: AriaAttributes['aria-sort']) => void;
  durationSortDirection: AriaAttributes['aria-sort'];
  onDurationSortClick: (sortDirection: AriaAttributes['aria-sort']) => void;
};

const InvocationListTableDesktop = ({
  invocations,
  startedAtSortDirection,
  onStartedAtSortClick,
  durationSortDirection,
  onDurationSortClick,
}: InvocationListTableDesktopProps) => {
  const {
    account: { slug: workspaceSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const {
    isBuildSpecific,
    isProjectSpecific,
    isFiltered,
    itemCount,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    selectedTool,
  } = useInvocationListTableContext();
  const hideProjectColumn = isBuildSpecific || isProjectSpecific;
  const isReactNative = selectedTool === BuildCacheToolIds.reactNative;
  const hideCacheStatusColumn = hidesCacheStatusColumn(selectedTool);
  const [selectedInvocation, setSelectedInvocation] = useState<BuildToolInvocationWithDetails | null>(null);
  const { open, onOpen, onClose } = useDisclosure();

  const handleOpenDrawer = (invocation: BuildToolInvocationWithDetails) => {
    segmentTrack('Invocation Details Panel Opened', {
      workspace_slug: workspaceSlug,
    });

    setSelectedInvocation(invocation);

    onOpen();
  };

  if (invocations.length === 0) {
    return (
      <BitkitEmptyState
        icon={IconFilter}
        headingText="No invocations match your criteria"
        bodyText={isFiltered ? 'Modify your filters to get results.' : 'Check if your selected build tool is correct'}
      />
    );
  }

  const isSortable = invocations.length > 1;

  return (
    <>
      <BitkitTable.ScrollArea>
        <BitkitTable.Root
          borderTopLeftRadius="0"
          borderTopRightRadius="0"
          data-testid={`${buildCacheTools[selectedTool].id}InvocationListTable`}
        >
          <BitkitTable.Header>
            <BitkitTable.Row>
              <BitkitTable.ColumnHeader width={rem(80)}>Status</BitkitTable.ColumnHeader>
              {!hideProjectColumn && <BitkitTable.ColumnHeader>Project</BitkitTable.ColumnHeader>}
              <BitkitTable.ColumnHeader>Command</BitkitTable.ColumnHeader>
              {isReactNative && (
                <BitkitTable.ColumnHeader width={rem(200)} minWidth={rem(200)}>
                  Target
                </BitkitTable.ColumnHeader>
              )}
              {!hideCacheStatusColumn && (
                <BitkitTable.ColumnHeader width={rem(140)}>Cache status</BitkitTable.ColumnHeader>
              )}
              {isSortable ? (
                <BitkitTable.SortableColumnHeader
                  direction={toSortDirection(durationSortDirection)}
                  onSort={() => onDurationSortClick(nextSortDirection(durationSortDirection))}
                  textAlign="end"
                  width={rem(140)}
                >
                  Duration
                </BitkitTable.SortableColumnHeader>
              ) : (
                <BitkitTable.ColumnHeader textAlign="end" width={rem(140)}>
                  Duration
                </BitkitTable.ColumnHeader>
              )}
              {isSortable ? (
                <BitkitTable.SortableColumnHeader
                  direction={toSortDirection(startedAtSortDirection)}
                  onSort={() => onStartedAtSortClick(nextSortDirection(startedAtSortDirection))}
                  width={rem(178)}
                >
                  Started at
                </BitkitTable.SortableColumnHeader>
              ) : (
                <BitkitTable.ColumnHeader width={rem(178)}>Started at</BitkitTable.ColumnHeader>
              )}
              {!isBuildSpecific && <BitkitTable.ColumnHeader width={rem(80)} />}
            </BitkitTable.Row>
          </BitkitTable.Header>
          <BitkitTable.Body>
            {invocations.map((invocation) => (
              <InvocationListTableRowDesktop
                key={invocation.invocationId}
                invocation={invocation}
                onOpenDrawer={handleOpenDrawer}
              />
            ))}
          </BitkitTable.Body>
          {itemCount > itemsPerPage && (
            <BitkitTable.Footer>
              <BitkitTable.Row>
                <BitkitTable.Cell
                  colSpan={
                    4 + // Status, Command, Duration, Started at
                    (hideProjectColumn ? 0 : 1) + // Project
                    (isBuildSpecific ? 0 : 1) + // Actions
                    (isReactNative ? 1 : 0) + // Target
                    (hideCacheStatusColumn ? 0 : 1) // Cache status
                  }
                >
                  <BitkitPagination
                    page={page}
                    pageSize={itemsPerPage}
                    totalItems={itemCount || 0}
                    onPageChange={setPage}
                    onPageSizeChange={setItemsPerPage}
                    pageSizeOptions={[10, 20, 50]}
                    variant="table"
                  />
                </BitkitTable.Cell>
              </BitkitTable.Row>
            </BitkitTable.Footer>
          )}
        </BitkitTable.Root>
      </BitkitTable.ScrollArea>
      {!isBuildSpecific && selectedInvocation && (
        <InvocationDetailsDrawer invocation={selectedInvocation} isOpen={open} onClose={onClose} />
      )}
    </>
  );
};

export default InvocationListTableDesktop;
